import I18n from 'I18n';
import { useRef, useState } from 'react';
import { LayoutType } from 'signup-components/types';
import { Step } from 'signup-constants/Step';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { useButtonGridFocus } from '../hooks/useButtonGridFocus';
import { useButtonGridPreferredColumnCount } from '../hooks/useButtonGridPreferredColumnCount';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { isTransitionAndAnimationsDisabled } from '../selenium/seleniumFeatures';
import { useSignupData } from '../signup-data/useSignupData';
import { ButtonsGridButton, ButtonsGridButtonHolder, ButtonsGridStyled, StyledButtonText, getButtonColumnCount } from '../styles/ButtonsGrid.styles';
import { useView } from '../views/useView';
import NextButtonWrapper from './NextButtonWrapper';
import { useTracker } from '../tracking/useTracker';
import { HubletToImageUrl } from '../hublets/hubletSelectionUtils';
import { useSelector } from 'react-redux';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { RecommendedLabel } from './RecommendedLabel';
import { Image } from 'foundations-components/transitional/components/Image';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const defaultGetButtonText = ({
  dataKey,
  key
}) => {
  return I18n.text(`views.${dataKey}.options.${key}`);
};
export const ButtonsGrid = ({
  columnCount,
  optionHeight,
  options,
  dataKey,
  stepKey,
  type,
  hasNextButton = false,
  nextButtonLabel,
  shouldDisableAutofocus = false,
  getButtonDescriptionText,
  getButtonText = defaultGetButtonText,
  onSelect = () => {},
  onHighlightChange = () => {},
  setDataFn,
  isSelected = () => false,
  shouldCenter = true,
  goToNextStep = true,
  shouldShowRecommendedLabel = () => false
}) => {
  const gridRef = useRef(null);
  const [clickedNextButton, setClickedNextButon] = useState(false);
  const {
    submit
  } = useView({
    stepKey
  });
  const {
    data,
    setData
  } = useSignupData(dataKey);
  const {
    preferredColumnCount
  } = useButtonGridPreferredColumnCount(options, dataKey, getButtonText);
  const {
    currentStep
  } = useCurrentStep();
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const isHubletSelectionStep = currentStep === Step.HubletSelection;
  const {
    isEmbedded,
    isActive
  } = useSignupEmbedder();
  const {
    isMobile
  } = useResponsiveness();
  const {
    layoutType
  } = useLayoutTemplate();
  const Tracker = useTracker();
  const isFramedCentredStepLayout = layoutType === LayoutType.FramedCentredStep;
  const actualColumnCount = columnCount || preferredColumnCount || getButtonColumnCount(currentStep, isMobile, isEmbedded, isFramedCentredStepLayout);
  const onSelectedChange = value => {
    setClickedNextButon(true);
    onSelect(value);
    submit();
  };
  useButtonGridFocus(gridRef, options.length, shouldDisableAutofocus);
  const onClickOption = (key, response) => {
    if (setDataFn) {
      setDataFn(key, setData);
    } else {
      setData(key);
    }
    if (!hasNextButton && goToNextStep) {
      onSelectedChange(key);
    } else {
      onHighlightChange(key);
      if (response && response.target.value) {
        Tracker.signupInteraction(`highlight-${dataKey}`, {
          selectedOption: [key]
        });
      } else {
        Tracker.signupInteraction(`deselect-${dataKey}`, {
          selectedOption: [key]
        });
      }
    }
  };
  const nthChild = actualColumnCount % 2 === 0 ? 'odd' : 'even';
  const buttonHeight = optionHeight;
  const renderButton = (key, index) => {
    return /*#__PURE__*/_jsxs(ButtonsGridButton, Object.assign({
      block: true,
      "data-selenium": `${dataKey}-${index}`,
      "data-selenium-option": key,
      description: getButtonDescriptionText && getButtonDescriptionText({
        dataKey,
        key,
        index
      }),
      disable: isTransitionAndAnimationsDisabled(),
      disabled: false,
      id: "ButtonsGrid_Button",
      name: 'selectable-button',
      selected: data === key || isSelected(key),
      title: /*#__PURE__*/_jsx(StyledButtonText, {
        isHubletButtonText: isHubletSelectionStep,
        children: getButtonText({
          dataKey,
          key,
          index
        })
      }),
      truncatable: false,
      type: type
    }, type === 'radio' ? {
      onClick: () => {
        onClickOption(key);
      }
    } : {
      onSelectedChange: response => {
        onClickOption(key, response);
      }
    }, {
      children: [shouldShowRecommendedLabel(key) && /*#__PURE__*/_jsx(RecommendedLabel, {}), isHubletSelectionStep && /*#__PURE__*/_jsx(Image, {
        paddingBlockStart: "100",
        paddingInlineStart: "1300",
        responsive: true,
        src: HubletToImageUrl[key]
      })]
    }));
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ButtonsGridStyled
    // @ts-expect-error Expression produces a union type that is too complex to represent.
    , {
      $addTopPadding: !isPostAccountCreationStep,
      $columnCount: columnCount,
      $currentStep: currentStep,
      $isFramedCentredStepLayout: isFramedCentredStepLayout,
      $layoutType: layoutType,
      $nthChild: nthChild,
      $offset: actualColumnCount - 1,
      $shouldCenter: shouldCenter && options.length % actualColumnCount !== 0,
      as: "div",
      embedded: isActive,
      ref: gridRef,
      children: options.map((key, index) => /*#__PURE__*/_jsx(ButtonsGridButtonHolder
      // @ts-expect-error Expression produces a union type that is too complex to represent.
      , {
        $currentStep: currentStep,
        minHeight: buttonHeight,
        children: renderButton(key, index)
      }, key))
    }), hasNextButton && /*#__PURE__*/_jsx(NextButtonWrapper, {
      label: nextButtonLabel,
      loading: clickedNextButton,
      onClick: () => onSelectedChange(data)
    })]
  });
};